import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import LeftIcon from '../assets/images/greyIcons/filled.png';
import RightIcon from '../assets/images/greyIcons/Icon.png';
import ArrowDown from '../assets/icons/down-filled.png';
import 'dayjs/locale/zh-cn';
import CustomTitle from './CustomTitle';
import CustomInput from './CustomInput';
import CustomParagraph from './CustomParagraph';
import { Calendar, Col, Radio, RadioChangeEvent, Row, Select } from 'antd';
import CustomSelect from './CustomSelect';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {
  HomeCalendarDateType,
  ReportingPeriodList,
} from '../common/static_data';
import { useToken } from '@/common/helper';
import { formatUserDateTime } from '@/common/utils/userDateFormat';
import CustomRadioGroup from './CustomRadioGroup';
import styles from './HomeDateFilter.module.scss';

dayjs.extend(isSameOrAfter);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

// Slash-separated numeric input regex per date_format preference — the
// digit grouping (2-2-4 vs 4-2-2) is handled separately in handleDateSelect.
const DATE_INPUT_REGEX: Record<string, RegExp> = {
  'MM/DD/YYYY': /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/,
  'DD/MM/YYYY': /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
  'YYYY/MM/DD': /^\d{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/,
};

/**
 * Data model for the date range selection sent to parent component
 */
interface DateRangeModel {
  start_date: string | null;
  end_date: string | null;
  period: string | undefined;
}

/**
 * Initial date configuration passed to the component
 */
export interface InitialDateModal {
  start_date?: string;
  end_date?: string;
  period?: string;
  date_type?: string;
}

/**
 * User task data containing min/max dates for "All time" period
 */
interface UserTask {
  min_date?: string;
  max_date?: string;
}

/**
 * Props for the HomeDateFilter component
 */
interface HomeDateFilterProps {
  keys: string;
  handleDataFromChild: (data: DateRangeModel) => void;
  initialDateModal?: InitialDateModal | null;
  userTask?: UserTask | null;
  showCompareTo?: boolean;
  compareTo?: 'previous_period' | 'previous_year' | string;
  onCompareToChange?: (value: 'previous_period' | 'previous_year') => void;
}

/**
 * A date filter component with calendar selection and predefined period options.
 * Supports custom date ranges and various reporting periods (Last 7 days, Month to date, etc.)
 *
 * @param keys - Unique key to trigger calendar reset
 * @param handleDataFromChild - Callback function to send selected date range to parent
 * @param initialDateModal - Initial date values to populate the component
 * @param userTask - User task data for "All time" period calculation
 */
const HomeDateFilter: React.FC<HomeDateFilterProps> = ({
  keys,
  handleDataFromChild,
  initialDateModal,
  userTask,
  showCompareTo = false,
  compareTo = 'previous_period',
  onCompareToChange,
}) => {
  const { decodedUserToken } = useToken();
  const user = decodedUserToken();
  const dateFormat = user?.date_format || 'MM/DD/YYYY';

  const formatDateForInput = useCallback(
    (dateStr?: string) => {
      if (!dateStr) return undefined;
      // Slash-separated input is our own round-tripped dateFormat output
      // (ambiguous otherwise, e.g. DD/MM/YYYY); dash-separated (ISO) input is
      // unambiguous and safe to parse without a format hint.
      const parsed = dateStr.includes('/')
        ? dayjs(dateStr, dateFormat)
        : dayjs(dateStr);
      return parsed.isValid()
        ? parsed.format(dateFormat)
        : dateStr.replace(/-/g, '/');
    },
    [dateFormat]
  );
  const [rangeStart, setRangeStart] = useState<string | null | undefined>(
    formatDateForInput(initialDateModal?.start_date)
  );
  const [period, setPeriod] = useState<string | undefined>(
    initialDateModal?.period
  );
  const [rangeEnd, setRangeEnd] = useState<string | null | undefined>(
    formatDateForInput(initialDateModal?.end_date)
  );

  const [dateError, setDateError] = useState<string>('');
  const buttonPress = useRef<boolean>(false);
  const [calendarValue, setCalendarValue] = useState<Dayjs>(dayjs());
  const [selectedDateType, setSelectedDateType] = useState<string | undefined>(
    initialDateModal?.date_type
  );

  const handleDateTypeChange = (e: RadioChangeEvent) => {
    const value = e.target.value as string;
    setSelectedDateType(value);
    sendDataToParent(rangeStart, rangeEnd, period, value);
  };

  // Reset calendar when period changes
  useEffect(() => {
    setCalendarValue(dayjs());
    setRangeStart(formatDateForInput(initialDateModal?.start_date));
    setRangeEnd(formatDateForInput(initialDateModal?.end_date));
    setPeriod(initialDateModal?.period);
    setDateError('');
  }, [
    keys,
    initialDateModal?.start_date,
    initialDateModal?.end_date,
    initialDateModal?.period,
    formatDateForInput,
  ]);

  // Get last 3 full months dynamically
  const now = new Date();
  const dynamicMonths = Array.from({ length: 3 }).map((_, i) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (i + 1), 1); // Previous full months
    const label = date.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });
    return { label, value: label };
  });

  const dynamicLastmonths = [...ReportingPeriodList, ...dynamicMonths];

  const onDateChange = (val: Dayjs) => {
    if (buttonPress.current === false) {
      let start = rangeStart;
      let end = rangeEnd;
      setDateError('');
      if (!rangeStart) {
        start = val.format(dateFormat);
        setRangeStart(val.format(dateFormat));
      } else if (!rangeEnd) {
        if (dayjs(val).isSameOrAfter(dayjs(rangeStart, dateFormat), 'day')) {
          end = val.format(dateFormat);
          setRangeEnd(val.format(dateFormat));
        } else {
          setDateError('Start range must be less than end range.');
        }
      } else {
        setRangeEnd(null);
        end = null;
        start = val.format(dateFormat);
        setRangeStart(val.format(dateFormat));
      }
      setPeriod('Custom');
      if (end !== null) {
        sendDataToParent(start, end, 'Custom', selectedDateType);
      }
    }
    buttonPress.current = false;
  };

  const handlePeriodChange = (data: string) => {
    const tz = user?.user_time_zone || dayjs.tz.guess();
    const today = dayjs().tz(tz);
    setPeriod(data);
    setCalendarValue(today);

    let endDate: string | null = today.format(dateFormat);
    let startDate: string | null = null;

    if (data === 'Today') {
      startDate = today.format(dateFormat);
      endDate = today.format(dateFormat);
    } else if (data === 'Yesterday') {
      startDate = today.subtract(1, 'day').format(dateFormat);
      endDate = today.subtract(1, 'day').format(dateFormat);
    } else if (data === 'Last week') {
      startDate = today.subtract(1, 'week').startOf('week').format(dateFormat);
      endDate = today.subtract(1, 'week').endOf('week').format(dateFormat);
    } else if (data === 'Last month') {
      startDate = today
        .subtract(1, 'month')
        .startOf('month')
        .format(dateFormat);
      endDate = today.subtract(1, 'month').endOf('month').format(dateFormat);
    } else if (data === 'Week to date') {
      startDate = today.startOf('week').format(dateFormat);
      endDate = today.format(dateFormat);
    } else if (data === 'Last 24 hours') {
      startDate = today.subtract(1, 'day').format(dateFormat);
      endDate = today.format(dateFormat);
    } else if (data === 'Last 7 days') {
      startDate = today.subtract(6, 'day').format(dateFormat);
    } else if (data === 'Last 30 days') {
      startDate = today.subtract(29, 'day').format(dateFormat);
    } else if (data === 'Month to date') {
      startDate = today.startOf('month').format(dateFormat);
    } else if (data === 'Quarter to date') {
      const month = today.month();
      const quarterStartMonth = Math.floor(month / 3) * 3;
      startDate = today
        .month(quarterStartMonth)
        .startOf('month')
        .format(dateFormat);
    } else if (data === 'Year to date') {
      const startOfYear = dayjs().startOf('year');
      startDate = startOfYear.format(dateFormat);
    } else if (data === 'Custom' || data === 'All time') {
      startDate = null;
      endDate = null;
    } else {
      const parsed = dayjs(data);
      if (parsed.isValid()) {
        startDate = parsed.startOf('month').format(dateFormat);
        endDate = parsed.endOf('month').format(dateFormat);
      } else {
        startDate = null;
        endDate = null;
      }
    }
    if (data === 'All time') {
      // Backend now returns ISO YYYY-MM-DD in the user timezone; legacy
      // responses may still be MM/DD/YYYY.
      const parseBound = (raw?: string) => {
        if (!raw) return null;
        const parsed = raw.includes('/')
          ? dayjs(raw, 'MM/DD/YYYY')
          : dayjs(raw);
        return parsed.isValid() ? parsed.format(dateFormat) : null;
      };
      startDate = parseBound(userTask?.min_date);
      endDate = parseBound(userTask?.max_date) || today.format(dateFormat);

      setRangeStart(startDate);
      setRangeEnd(endDate);
    } else {
      setRangeStart(startDate);
      setRangeEnd(endDate);
    }
    sendDataToParent(startDate, endDate, data, selectedDateType);
  };

  const sendDataToParent = (
    start: string | null | undefined,
    end: string | null | undefined,
    period: string | undefined,
    dateType: string | undefined
  ) => {
    if (period === 'Custom' && (!start || !end)) {
      return; // don’t send to parent until both dates are selected in custom case
    }
    const rangeModel = {
      start_date: start ?? null,
      end_date: end ?? null,
      period: period,
      date_type: dateType,
    };

    handleDataFromChild(rangeModel);
  };

  const getCompareWindowLabel = (
    value: 'previous_period' | 'previous_year'
  ) => {
    const start = rangeStart ? dayjs(rangeStart, dateFormat) : null;
    const end = rangeEnd ? dayjs(rangeEnd, dateFormat) : null;

    if (!start?.isValid() || !end?.isValid()) return '';

    const dateOnly = { includeTime: false };

    if (value === 'previous_year') {
      return `${formatUserDateTime(start.subtract(1, 'year'), dateFormat, dateOnly)} - ${formatUserDateTime(end.subtract(1, 'year'), dateFormat, dateOnly)}`;
    }

    const dayCount = end.diff(start, 'day') + 1;
    const previousEnd = start.subtract(1, 'day');
    const previousStart = previousEnd.subtract(
      Math.max(dayCount - 1, 0),
      'day'
    );

    return `${formatUserDateTime(previousStart, dateFormat, dateOnly)} - ${formatUserDateTime(previousEnd, dateFormat, dateOnly)}`;
  };

  const handleDateSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'start' | 'end'
  ) => {
    // dont remove comments these are important for understanding
    // adding to enable start and end date inputs -
    // remove non-digits just rcv digits
    let input = e.target.value.replace(/\D/g, '');

    // Limit to 8 digits for date (YYYY/MM/DD or MM/DD/YYYY, same digit count)
    if (input.length > 8) input = input.slice(0, 8);

    // Auto-insert slashes, grouped per the date_format preference:
    // year-first is 4-2-2 (e.g. 2025/03/21), everything else is 2-2-4 (e.g. 03/21/2025)
    const isYearFirst = dateFormat.startsWith('YYYY');
    if (isYearFirst) {
      if (input.length > 6) {
        input = input.replace(/^(\d{4})(\d{2})(\d+)/, '$1/$2/$3');
      } else if (input.length > 4) {
        input = input.replace(/^(\d{4})(\d+)/, '$1/$2');
      }
    } else if (input.length > 4) {
      input = input.replace(/^(\d{2})(\d{2})(\d+)/, '$1/$2/$3');
    } else if (input.length > 2) {
      input = input.replace(/^(\d{2})(\d+)/, '$1/$2');
    }

    // Update state immediately so user sees the slashes
    if (type === 'start') {
      setRangeStart(input);
    } else {
      setRangeEnd(input);
    }

    const dateRegex = DATE_INPUT_REGEX[dateFormat];

    // Validate input format and logical date
    if (dateRegex.test(input) && dayjs(input, dateFormat, true).isValid()) {
      setDateError(''); // Clear error

      const formatted = dayjs(input, dateFormat).format(dateFormat); // Keep formating consistent

      // Check start and end relationship
      const startValid = dayjs(rangeStart, dateFormat, true).isValid()
        ? dayjs(rangeStart, dateFormat)
        : null;
      const endValid = dayjs(rangeEnd, dateFormat, true).isValid()
        ? dayjs(rangeEnd, dateFormat)
        : null;

      if (type === 'start' && endValid && formatted) {
        if (dayjs(formatted, dateFormat).isAfter(endValid)) {
          setDateError('Start date must be less than end date.');
          return;
        } else {
          setDateError('');
        }
      }

      if (type === 'end' && startValid && formatted) {
        if (dayjs(formatted, dateFormat).isBefore(startValid)) {
          setDateError('End date must be greater than start date.');
          return;
        } else {
          setDateError('');
        }
      }

      if (type === 'start') {
        const endValid = dayjs(rangeEnd, dateFormat, true).isValid()
          ? dayjs(rangeEnd, dateFormat).format(dateFormat)
          : '';

        sendDataToParent(formatted, endValid, period, selectedDateType);
        setCalendarValue(dayjs()); // Reset calendar to current date when period changes
      } else {
        const startValid = dayjs(rangeStart, dateFormat, true).isValid()
          ? dayjs(rangeStart, dateFormat).format(dateFormat)
          : '';

        sendDataToParent(startValid, formatted, period, selectedDateType);
        setCalendarValue(dayjs()); // Reset calendar to current date when period changes
      }
    } else {
      setDateError(`Please enter a valid date in ${dateFormat} format.`);
    }
  };

  return (
    <div className={styles.body}>
      <Calendar
        fullscreen={false}
        value={calendarValue}
        onChange={(newValue) => setCalendarValue(newValue)}
        fullCellRender={(date) => {
          const cellDate = dayjs(date);
          const start = rangeStart ? dayjs(rangeStart, dateFormat) : null;
          const end = rangeEnd ? dayjs(rangeEnd, dateFormat) : null;

          const isInRange =
            start &&
            end &&
            !cellDate.isBefore(start, 'day') &&
            !cellDate.isAfter(end, 'day');

          return (
            <div
              className={`xyz ${isInRange ? 'highlight-range' : ''}
                                       ${
                                         start && cellDate.isSame(start, 'day')
                                           ? 'range-cell-customization'
                                           : ''
                                       }
                                        ${
                                          end && cellDate.isSame(end, 'day')
                                            ? 'range-cell-customization'
                                            : ''
                                        }`}
            >
              {cellDate.date()}
            </div>
          );
        }}
        headerRender={({ value, onChange }) => {
          const start = 0;
          const end = 12;
          const monthOptions = [];

          const months = dayjs.monthsShort();

          for (let i = start; i < end; i++) {
            monthOptions.push(
              <Select.Option key={i} value={i} className='month-item'>
                {months[i]}
              </Select.Option>
            );
          }

          const handlePrev = () => {
            buttonPress.current = true;
            const newValue = value.clone().subtract(1, 'month');
            onChange(newValue); // Update the calendar to the new value
          };

          const handleNext = () => {
            buttonPress.current = true;
            const newValue = value.clone().add(1, 'month'); // Add one month to the current value
            onChange(newValue); // Update the calendar to the new value
          };

          const year = value.year();
          const month = value.month();
          const options = [];
          for (let i = year - 10; i < year + 10; i += 1) {
            options.push(
              <Select.Option key={i} value={i} className='year-item'>
                {i}
              </Select.Option>
            );
          }
          return (
            <Row
              gutter={8}
              style={{
                paddingBottom: '16px',
                paddingTop: '10px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Col
                span={11}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <button
                  className={styles.navBtn}
                  type='button'
                  onClick={handlePrev}
                >
                  <img
                    className='zl-invert-on-dark'
                    width='12px'
                    src={LeftIcon}
                  />
                </button>
                <Select
                  style={{ width: '55%' }}
                  popupMatchSelectWidth={false}
                  value={month}
                  onChange={(newMonth) => {
                    buttonPress.current = true;
                    const now = value.clone().month(newMonth);
                    onChange(now);
                  }}
                  suffixIcon={
                    <img className='zl-invert-on-dark' src={ArrowDown} />
                  }
                  className='date-select-month'
                >
                  {monthOptions}
                </Select>
              </Col>
              <Col
                span={11}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Select
                  style={{ width: '60%' }}
                  popupMatchSelectWidth={false}
                  className='my-year-select date-select-month'
                  value={year}
                  onChange={(newYear) => {
                    buttonPress.current = true;
                    const now = value.clone().year(newYear);
                    onChange(now);
                  }}
                  suffixIcon={
                    <img className='zl-invert-on-dark' src={ArrowDown} />
                  }
                >
                  {options}
                </Select>

                <button
                  className={styles.navBtn}
                  type='button'
                  onClick={handleNext}
                >
                  <img
                    className='zl-invert-on-dark'
                    width='26px'
                    src={RightIcon}
                  />
                </button>
              </Col>
            </Row>
          );
        }}
        onSelect={onDateChange}
      />
      <Row>
        <Col span={24}>
          <CustomTitle
            text={'Reporting period'}
            color='var(--text-primary)'
            fontSize='12px'
            lineHeight='20px'
            fontWeight='600'
            marginBottom={0}
          />
          <CustomSelect
            placeholder='Choose period'
            value={period}
            onChange={handlePeriodChange}
            allowClear={false}
            lineHeight={'16px'}
            border='0px'
            bgColor={'var(--light-gray)'}
            borderRadius={'4px'}
            boxShadow={'none'}
            height='28px'
            // options={ReportingPeriodList}
            options={dynamicLastmonths}
            suffixIcon={<img className='zl-invert-on-dark' src={ArrowDown} />}
          />
        </Col>
      </Row>

      <Row
        style={{
          margin: '0px',
          display: 'Flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Col span={11}>
          <CustomTitle
            text={'Start'}
            color='var(--text-primary)'
            fontSize='12px'
            lineHeight='20px'
            fontWeight='600'
            marginBottom={0}
          />
          <CustomInput
            placeholder={dateFormat.toLowerCase()}
            height='28px'
            width='100%'
            type='text'
            border='0px'
            bgColor='var(--light-gray)'
            textColor='var(--text-primary)'
            borderRadius='4px'
            placeholderColor='var(--text-secondary)'
            lineHeight='38px'
            value={rangeStart ?? undefined} // Should be "MM/DD/YYYY"
            onChange={(e) => handleDateSelect(e, 'start')}
            onFocus={() => setPeriod('Custom')}
            // disabled={period !== 'Custom'}
          />
        </Col>
        <Col span={11}>
          <CustomTitle
            text={'End'}
            color='var(--text-primary)'
            fontSize='12px'
            lineHeight='20px'
            fontWeight='600'
            marginBottom={0}
          />
          <CustomInput
            placeholder={dateFormat.toLowerCase()}
            height='28px'
            width='100%'
            type='text'
            border='0px'
            bgColor='var(--light-gray)'
            textColor='var(--text-primary)'
            borderRadius='4px'
            placeholderColor='var(--text-secondary)'
            lineHeight='38px'
            value={rangeEnd ?? undefined} // Should be "MM/DD/YYYY"
            onChange={(e) => handleDateSelect(e, 'end')}
            onFocus={() => setPeriod('Custom')}
            // disabled={period !== 'Custom'}
          />
        </Col>
        {dateError ? (
          <Col span={24}>
            <CustomParagraph
              text={dateError}
              color='var(--danger)'
              fontSize='12px'
              fontWeight='400'
              lineHeight='28px'
              marginBottom={0}
            />
          </Col>
        ) : null}
        <Col span={24}>
          <CustomParagraph
            text={'Date type'}
            fontWeight='600'
            fontSize='12px'
            lineHeight='16px'
            marginTop='16px'
            marginBottom='12px'
          />
          <CustomRadioGroup
            value={selectedDateType}
            onChange={handleDateTypeChange}
            options={HomeCalendarDateType}
          />
        </Col>
        {showCompareTo ? (
          <Col span={24}>
            <div className='home-date-compare-to'>
              <CustomParagraph
                text='Compare to'
                fontWeight='600'
                fontSize='12px'
                lineHeight='16px'
                marginTop='16px'
                marginBottom='12px'
              />
              <Radio.Group
                value={compareTo}
                onChange={(event) => onCompareToChange?.(event.target.value)}
              >
                <div className='home-date-compare-option'>
                  <Radio value='previous_period'>Previous period</Radio>
                  <span>{getCompareWindowLabel('previous_period')}</span>
                </div>
                <div className='home-date-compare-option'>
                  <Radio value='previous_year'>Previous year</Radio>
                  <span>{getCompareWindowLabel('previous_year')}</span>
                </div>
              </Radio.Group>
            </div>
          </Col>
        ) : null}
      </Row>
    </div>
  );
};

export default HomeDateFilter;
